import React, { useRef, useEffect, useState } from "react";
import { useSlate, useFocused, ReactEditor } from "slate-react";
import { createPortal } from "react-dom";
import { Editor, Range, Transforms } from "slate";
import { elevations } from "@toddle-edu/ds-theme";
import { IconButton } from "@toddle-edu/ds-web";
import _ from "lodash";
import classes from "./HoveringToolbar.scss";
import { toggleFormat, isFormatActive } from "../InlineEditorUtils";
import {
  EDITOR_OPTION_DETAILS,
  EDITOR_OPTION_LINK,
} from "../InlineEditorConstants";
import AddEditLinkModal from "../AddEditLinkModal";

const MODAL_TYPES = {
  LINK: "LINK",
};

const HoveringToolbar = ({ editorToolbarOptions }) => {
  const ref = useRef();
  const editor = useSlate();
  const inFocus = useFocused();

  const [showModal, setShowModal] = useState(false);
  const onModalClose = () => {
    setShowModal("");
    // Move the selection to the end of the editor
    Transforms.select(editor, Editor.end(editor, []));
    // Focus the editor
    ReactEditor.focus(editor);
  };

  useEffect(() => {
    const el = ref.current;
    const { selection } = editor;

    if (!el) {
      return;
    }

    if (
      !selection ||
      !inFocus ||
      Range.isCollapsed(selection) ||
      Editor.string(editor, selection) === ""
    ) {
      el.style.opacity = "0";
      el.style.top = `-9999px`;
      el.style.left = `-9999px`;
      return;
    }

    const domSelection = window.getSelection();
    if (_.get(domSelection, "rangeCount", 0) > 0) {
      const domRange = domSelection?.getRangeAt?.(0);
      const rect = domRange.getBoundingClientRect();
      el.style.opacity = "1";
      el.style.top = `${rect.top - el.offsetHeight}px`;
      el.style.left = `${rect.left - el.offsetWidth / 2 + rect.width / 2}px`;
    }
  });

  const getModal = () => {
    switch (showModal) {
      case MODAL_TYPES.LINK:
        return <AddEditLinkModal onClose={onModalClose} />;
      default:
        return null;
    }
  };

  return !_.isEmpty(showModal)
    ? getModal()
    : createPortal(
        <div
          ref={ref}
          className={classes.menu}
          style={elevations["elevation-2B"]}
          onMouseDown={e => {
            // prevent toolbar from taking focus away from editor
            e.preventDefault();
          }}
        >
          {_.map(editorToolbarOptions, editorOption => {
            const { format, icon } = _.get(
              EDITOR_OPTION_DETAILS,
              editorOption,
              {}
            );
            const isActive = format && isFormatActive(editor, format);
            const onButtonClick = () => {
              switch (editorOption) {
                case EDITOR_OPTION_LINK:
                  setShowModal(MODAL_TYPES.LINK);
                  break;
                default:
                  toggleFormat(editor, format);
                  break;
              }
            };
            return (
              <IconButton
                key={editorOption}
                icon={icon}
                _legacy_variant={"plain"}
                _legacy_size={"x-small"}
                type="plain"
                variant="neutral"
                size="small"
                onClick={onButtonClick}
                isActivated={isActive}
              />
            );
          })}
        </div>,
        document.body
      );
};

export default HoveringToolbar;
